import { LISTS_DOC, listDocName, type ListItem, type ListMeta } from '@awwyeah/shared';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocManager, useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import { createListMeta, instantiateTemplate } from './listOps';
import { LIST_TEMPLATES, templateItemCount, type ListTemplate } from './templates';

function allSelectedKeys(template: ListTemplate): Set<string> {
  const keys = new Set<string>();
  template.groups.forEach((group, gi) => group.items.forEach((_, ii) => keys.add(`${gi}-${ii}`)));
  return keys;
}

export function TemplatesPage(): JSX.Element {
  const { user } = useUser();
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useState(LIST_TEMPLATES[0]?.id ?? '');
  const template = LIST_TEMPLATES.find((t) => t.id === selectedTemplateId) ?? null;
  const [selected, setSelected] = useState<Set<string>>(() =>
    template ? allSelectedKeys(template) : new Set(),
  );
  const [listName, setListName] = useState(template?.name ?? '');

  const listsDoc = useYDoc(LISTS_DOC);
  const { map: listsMap } = useYMap<ListMeta>(listsDoc, 'lists');

  const pick = (t: ListTemplate): void => {
    setSelectedTemplateId(t.id);
    setSelected(allSelectedKeys(t));
    setListName(t.name);
  };

  const toggle = (key: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const create = (): void => {
    if (!user || !template) return;
    const meta = createListMeta({
      name: listName.trim() || template.name,
      type: template.type,
      userId: user.id,
    });
    listsMap.set(meta.id, meta);
    // The new list's own doc isn't mounted anywhere yet (we're about to navigate there),
    // so grab it straight from the manager rather than via the `useYDoc` hook — `doc()` is
    // safe to call outside an effect and starts IndexedDB persistence immediately, so the
    // items below are never lost even though nothing has `acquire()`d the doc yet.
    const listDoc = getDocManager().doc(listDocName(meta.id));
    const order = listDoc.getArray<string>('order');
    const map = listDoc.getMap<ListItem>('items');
    instantiateTemplate(listDoc, order, map, template.groups, selected, user.id);
    navigate(`/lists/${meta.id}`);
  };

  if (!user) return <></>;

  const pickedCount = selected.size;

  return (
    <div className="flex min-h-dvh flex-col bg-bg" data-testid="templates-page">
      <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-line bg-surface px-3">
        <button
          type="button"
          aria-label="Back"
          data-testid="templates-back"
          onClick={() => navigate(-1)}
          className="flex h-11 w-11 items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="font-display text-lg tracking-tight text-fg">Start from a template</span>
      </header>

      <div className="grid grid-cols-2 gap-2 p-4">
        {LIST_TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid="template-card"
            data-template-id={t.id}
            onClick={() => pick(t)}
            className={`flex min-h-[76px] flex-col gap-1 rounded-md border bg-surface p-3 text-left ${
              t.id === selectedTemplateId ? 'border-2 border-line-strong' : 'border-line'
            }`}
          >
            <span className="text-base font-semibold text-fg">
              {t.emoji} {t.name}
            </span>
            <span className="text-xs text-fg-3">
              {templateItemCount(t)} items · {t.groups.length} groups
            </span>
          </button>
        ))}
      </div>

      {template && (
        <div className="flex min-h-0 flex-1 flex-col border-t border-line bg-surface">
          <div className="flex flex-col gap-2 border-b border-line px-4 pb-3 pt-3">
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-fg">{template.name}</span>
              <span className="text-sm text-fg-3">
                {pickedCount} of {templateItemCount(template)} picked
              </span>
            </div>
            <input
              data-testid="template-list-name-input"
              className="h-11 rounded border border-line bg-bg px-3 text-base text-fg"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder={template.name}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {template.groups.map((group, gi) => (
              <div key={group.name}>
                <div className="flex h-8 items-center bg-bg px-4">
                  <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">
                    {group.name} · {group.items.length}
                  </span>
                </div>
                {group.items.map((item, ii) => {
                  const key = `${gi}-${ii}`;
                  const picked = selected.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid="template-item-toggle"
                      data-picked={picked}
                      onClick={() => toggle(key)}
                      className="flex min-h-[52px] w-full items-center gap-3 border-b border-line px-4 py-1.5 text-left"
                    >
                      <span
                        className={`flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded border-2 ${
                          picked ? 'border-sync-ok bg-sync-ok' : 'border-line'
                        }`}
                      >
                        {picked && (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={3}
                            strokeLinecap="round"
                            className="text-surface"
                          >
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className={`flex-1 text-base ${picked ? 'text-fg' : 'text-fg-3'}`}>
                        {item.text}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex flex-shrink-0 flex-col gap-2 border-t border-line px-4 py-3 pb-[max(16px,env(safe-area-inset-bottom))]">
            <button
              type="button"
              data-testid="create-from-template"
              disabled={pickedCount === 0}
              onClick={create}
              className="h-[52px] rounded-md bg-inverse text-base font-semibold text-inverse-fg disabled:opacity-50"
            >
              Create with {pickedCount} item{pickedCount === 1 ? '' : 's'}
            </button>
            <span className="text-center text-xs text-fg-3">
              Groups come along. You can still add your own after.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
